/* Page navigation only. Does not submit product forms or change cart state. */
(() => {
  if (customElements.get('sunpura-product-nav')) return;
  customElements.define('sunpura-product-nav', class extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      const abort = new AbortController(), signal = abort.signal;
      const on = (el, name, fn, opts = {}) => el?.addEventListener(name, fn, { ...opts, signal });
      const wrapper = this.closest('.shopify-section') || this;
      const viewport = this.querySelector('[data-nav-viewport]'), track = this.querySelector('[data-nav-track]');
      const indicator = this.querySelector('[data-nav-indicator]'), slot = this.querySelector('[data-nav-buy-slot]');
      const prev = this.querySelector('[data-nav-prev]'), next = this.querySelector('[data-nav-next]');
      if (!viewport || !track || !indicator) return;
      const links = [...this.querySelectorAll('[data-nav-link]')], main = document.getElementById('MainContent') || document.querySelector('main');
      let headers = [];
      try { headers = [...document.querySelectorAll(this.dataset.headerSelector || '.section-header')]; } catch { /* Invalid optional selector does not break navigation. */ }
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const motion = () => this.dataset.motion !== 'false' && !reduced.matches;
      const gap = Number(this.dataset.gap) || 16, extra = Math.max(0, Number(this.dataset.extraTop) || 0);
      let items = [], active = null, lock = null, frame = 0, refreshFrame = 0, headerUntil = 0, focusRestore = null;
      const originalTop = wrapper.style.getPropertyValue('--sp-nav-top');
      const resolve = link => {
        let target = document.getElementById(link.dataset.target);
        if (!target && link.hasAttribute('data-nav-buy')) target = main?.querySelector('[id^="MainProduct-"]') || document.querySelector('[id^="MainProduct-"]');
        if (!target && link.dataset.originalTarget === 'hems-ecosystem') target = document.getElementById('app-smart-control') || document.getElementById('hems-ecosystem');
        return target;
      };
      const visible = target => target?.isConnected && target.getClientRects().length > 0 && !target.closest('[hidden]');
      const top = target => target.getBoundingClientRect().top + scrollY;
      const headerOffset = () => {
        let bottom = 0;
        if (this.dataset.followHeader !== 'false') headers.forEach(header => {
          const style = getComputedStyle(header), rect = header.getBoundingClientRect();
          if (['sticky','fixed'].includes(style.position) && style.visibility !== 'hidden' && rect.top <= 1 && rect.bottom > 0) bottom = Math.max(bottom, rect.bottom);
        });
        return bottom + extra;
      };
      const offset = () => headerOffset() + wrapper.getBoundingClientRect().height + gap;
      const destination = item => Math.min(Math.max(0, top(item.target) - offset()), Math.max(0, document.documentElement.scrollHeight - innerHeight));
      const controls = () => {
        const overflow = viewport.scrollWidth > viewport.clientWidth + 2;
        prev.hidden = next.hidden = !overflow;
        prev.disabled = viewport.scrollLeft <= 2;
        next.disabled = viewport.scrollLeft >= viewport.scrollWidth - viewport.clientWidth - 2;
      };
      const keepVisible = (link, instant = false) => {
        if (!track.contains(link)) return;
        const left = link.offsetLeft, right = left + link.offsetWidth;
        if (left < viewport.scrollLeft + 4 || right > viewport.scrollLeft + viewport.clientWidth - 4) {
          viewport.scrollTo({ left: Math.max(0, left - (viewport.clientWidth - link.offsetWidth) / 2), behavior: motion() && !instant ? 'smooth' : 'instant' });
        }
      };
      const paint = (item, instant = false, reveal = true) => {
        const changed = item !== active;
        active = item;
        links.forEach(link => { if (link === item?.link) link.setAttribute('aria-current','location'); else link.removeAttribute('aria-current'); });
        if (!item || !track.contains(item.link)) indicator.hidden = true;
        else {
          const first = indicator.hidden;
          indicator.classList.toggle('is-instant', instant || first || !motion());
          indicator.style.setProperty('--sp-n-indicator-x', item.link.offsetLeft + 'px');
          indicator.style.setProperty('--sp-n-indicator-width', item.link.offsetWidth + 'px');
          indicator.style.setProperty('--sp-n-indicator-height', item.link.offsetHeight + 'px');
          indicator.hidden = false;
        }
        if (item && reveal && (changed || instant)) keepVisible(item.link, instant);
      };
      const focusTarget = item => {
        focusRestore?.();
        const anchor = item.target;
        const target = anchor.getAttribute('aria-hidden') === 'true' ? anchor.parentElement.querySelector('h1,h2,h3') : anchor.querySelector('h1,h2,h3') || anchor;
        if (!target) return;
        const prior = target.getAttribute('tabindex');
        if (prior === null) target.setAttribute('tabindex','-1');
        target.focus({ preventScroll:true });
        focusRestore = () => { if (prior === null) target.removeAttribute('tabindex'); focusRestore = null; };
        on(target,'blur', () => focusRestore?.(), { once:true });
      };
      const finish = () => { const current = lock; lock = null; if (current?.focus) focusTarget(current.item); };
      const tick = () => {
        frame = 0;
        const now = performance.now();
        wrapper.style.setProperty('--sp-nav-top', headerOffset() + 'px');
        if (lock) {
          paint(lock.item);
          const y = scrollY, desired = destination(lock.item);
          lock.still = Math.abs(y - lock.y) < .5 ? lock.still + 1 : 0; lock.y = y;
          if (now - lock.start > 150 && lock.still >= 6) {
            if (Math.abs(y - desired) > 3 && lock.corrections < 3) {
              scrollTo({ top:desired, behavior:'instant' }); lock.corrections++; lock.still = 0;
            } else finish();
          }
          if (lock && now - lock.start > 2600) finish();
        }
        if (!lock) {
          const ordered = items.filter(item => visible(item.target)).sort((a,b) => top(a.target) - top(b.target));
          const line = scrollY + offset() + 3;
          let current = ordered[0] || null;
          ordered.forEach(item => { if (top(item.target) <= line) current = item; });
          if (scrollY > 0 && scrollY + innerHeight >= document.documentElement.scrollHeight - 3) current = ordered.at(-1) || null;
          paint(current);
        }
        controls();
        if (lock || now < headerUntil) frame = requestAnimationFrame(tick);
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(tick); };
      const refresh = () => {
        refreshFrame = 0;
        const focused = links.includes(document.activeElement) ? document.activeElement : null;
        const seen = new Set();
        items = links.map(link => {
          const target = resolve(link), ok = visible(target) && !seen.has(target);
          link.hidden = !ok;
          if (!ok) return null;
          seen.add(target);
          link.href = '#' + encodeURIComponent(target.id);
          return { link, target };
        }).filter(Boolean);
        if (this.dataset.sort !== 'false') items.sort((a,b) => top(a.target) - top(b.target));
        items.forEach(item => {
          const pinned = this.dataset.pinBuy !== 'false' && item.link.hasAttribute('data-nav-buy');
          const parent = pinned ? slot : track;
          parent.append(item.link);
        });
        slot.hidden = !slot.querySelector('[data-nav-link]:not([hidden])');
        this.hidden = items.length === 0;
        if (lock) { const replacement = items.find(i => i.link === lock.item.link); if (replacement) lock.item = replacement; else finish(); }
        paint(items.find(i => i.link === active?.link) || null, true);
        if (focused && !focused.hidden) focused.focus({ preventScroll:true });
        schedule();
      };
      const requestRefresh = () => { if (!refreshFrame) refreshFrame = requestAnimationFrame(refresh); };
      const navigate = (item, push, focus) => {
        if (!visible(item.target)) { requestRefresh(); return; }
        if (push && location.hash !== '#' + encodeURIComponent(item.target.id)) history.pushState(history.state,'','#' + encodeURIComponent(item.target.id));
        lock = { item, start:performance.now(), y:scrollY, still:0, corrections:0, focus };
        paint(item,false,true);
        scrollTo({ top:destination(item), behavior:motion() ? 'smooth' : 'instant' });
        schedule();
      };
      const cancel = () => {
        if (lock) { lock = null; scrollTo({ top:scrollY, behavior:'instant' }); schedule(); }
      };
      const fromHash = () => {
        let id;try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
        const item = items.find(i => i.target.id === id || i.link.dataset.originalTarget === id);
        if (item) navigate(item,false,false); else schedule();
      };
      on(this,'click', event => {
        const link = event.target.closest('[data-nav-link]');
        if (!link || !this.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const item = items.find(i => i.link === link);
        if (item) { event.preventDefault(); navigate(item,true,event.detail === 0); }
      });
      on(prev,'click',()=>viewport.scrollBy({left:-viewport.clientWidth*.75,behavior:motion()?'smooth':'instant'}));
      on(next,'click',()=>viewport.scrollBy({left:viewport.clientWidth*.75,behavior:motion()?'smooth':'instant'}));
      on(viewport,'scroll',controls,{passive:true});
      on(window,'scroll',schedule,{passive:true});on(window,'resize',requestRefresh,{passive:true});
      on(window,'wheel',cancel,{passive:true});on(window,'touchstart',cancel,{passive:true});on(window,'pointerdown',cancel,{passive:true});
      on(window,'keydown',event=>{if(['PageUp','PageDown','Home','End','ArrowUp','ArrowDown','Escape'].includes(event.key))cancel();});
      on(window,'hashchange',fromHash);
      on(reduced,'change',()=>{cancel();requestRefresh();});
      on(document,'shopify:section:load',requestRefresh);on(document,'shopify:section:unload',requestRefresh);on(document,'shopify:section:reorder',requestRefresh);
      on(document,'shopify:block:select',event=>{if(this.contains(event.target)){const link=event.target.closest('[data-nav-link]');if(link)keepVisible(link,true);}});
      on(main,'load',requestRefresh,{capture:true});
      const resize = new ResizeObserver(requestRefresh);
      resize.observe(this.querySelector('nav'));if(main)resize.observe(main);headers.forEach(h=>resize.observe(h));
      const mutation = new MutationObserver(()=>{headerUntil=performance.now()+250;schedule();});
      headers.forEach(h=>mutation.observe(h,{attributes:true,attributeFilter:['class','style']}));
      const visibility = new IntersectionObserver(entries=>{this.dataset.inView=String(entries[0].isIntersecting);});visibility.observe(this);
      this.cleanup = () => {
        abort.abort();resize.disconnect();mutation.disconnect();visibility.disconnect();
        cancelAnimationFrame(frame);cancelAnimationFrame(refreshFrame);focusRestore?.();
        links.forEach(link=>{link.hidden=false;link.removeAttribute('aria-current');track.append(link);});
        indicator.hidden=true;slot.hidden=true;prev.hidden=next.hidden=true;this.hidden=false;
        if(originalTop)wrapper.style.setProperty('--sp-nav-top',originalTop);else wrapper.style.removeProperty('--sp-nav-top');
      };
      refresh();
      document.fonts?.ready.then(()=>{if(!signal.aborted)requestRefresh();});
      if(document.readyState==='complete')fromHash();else on(window,'load',()=>{refresh();fromHash();},{once:true});
    }
    disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
  });
})();
